import { IconPause } from 'assets/img';
import type { Column } from 'react-table';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { getExpenseWithNameAndValue } from 'shared/utils/expenses';
import { TFunctionNonStrict } from 'i18next';
import {
  PartnerCheckedOutCellProp,
  PartnerCheckedOutColumnDefinition,
} from '../partner-checked-out.types';

const getPartnerCheckedOutColumnDefinition: (
  allowExpenses: boolean,
  t: TFunctionNonStrict<'partner-screen-specific', undefined>
) => Column<PartnerCheckedOutColumnDefinition>[] = (allowExpenses, t) => {
  const worker = {
    Header: () => (
      <Header>{t('checked_out.table_headers.worker', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'flexWorkerName',
    Cell: WorkerCell,
    width: '100px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const job = {
    Header: () => (
      <Header>{t('checked_out.table_headers.job_title', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'jobTitle',
    Cell: JobCell,
    width: '75px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const company = {
    Header: () => (
      <Header>{t('checked_out.table_headers.company', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'companyName',
    Cell: CompanyNameCell,
    width: '100px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const branchOfficeName = {
    Header: () => (
      <Header>
        {t('checked_out.table_headers.branch_office', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'branchOfficeName',
    Cell: BranchOfficeNameCell,
    width: '100px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const date = {
    Header: () => (
      <Header>
        {t('checked_out.table_headers.start_datum', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'shiftStartDate',
    Cell: DateCell,
    width: '60px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const scheduledTime = {
    Header: () => (
      <Header>
        {t('checked_out.table_headers.shift_time', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'scheduledDateTime',
    Cell: ScheduledTimeCell,
    width: '125px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const workedTime = {
    Header: () => (
      <Header>
        {t('checked_out.table_headers.time_worked', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'workedDateTime',
    Cell: ScheduledTimeCell,
    width: '125px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const expenses = {
    Header: () => (
      <Header>{t('checked_out.table_headers.expenses', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'expense',
    Cell: ExpensesCell(allowExpenses),
    width: '120px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  const state = {
    Header: () => (
      <Header>{t('checked_out.table_headers.status', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'status',
    Cell: StateCell,
    width: '75px',
  } as Column<PartnerCheckedOutColumnDefinition>;

  return [worker, job, company, branchOfficeName, date, scheduledTime, workedTime, expenses, state];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <EllipsizedTypography variant="body2" style={{ fontWeight: 'bold' }}>
    {children}
  </EllipsizedTypography>
);

const WorkerCell = (cell: PartnerCheckedOutCellProp<'flexWorkerName'>) => {
  return (
    <EllipsizedTypography variant="body1" noWrap>
      {cell.value}
    </EllipsizedTypography>
  );
};

const CompanyNameCell = (cell: PartnerCheckedOutCellProp<'companyName'>) => {
  return (
    <EllipsizedTypography variant="body1" noWrap>
      {cell.value}
    </EllipsizedTypography>
  );
};
const BranchOfficeNameCell = (cell: PartnerCheckedOutCellProp<'branchOfficeName'>) => {
  return (
    <EllipsizedTypography variant="body1" noWrap>
      {cell.value}
    </EllipsizedTypography>
  );
};

const JobCell = (cell: PartnerCheckedOutCellProp<'jobTitle'>) => {
  return (
    <EllipsizedTypography variant="body1" noWrap>
      {cell.value}
    </EllipsizedTypography>
  );
};

const ExpensesCell = (allowExpenses: boolean) => (cell: PartnerCheckedOutCellProp<'expense'>) => {
  return (
    <>
      {cell.value?.length && (allowExpenses || cell.value.some((i) => i.amount !== 0))
        ? cell.value.map((expense) => {
            const expenseNameAndValue = getExpenseWithNameAndValue(expense);
            return (
              <Tooltip key={expense.expenseType} title={expenseNameAndValue}>
                <EllipsizedTypography variant="body1" noWrap>
                  {expenseNameAndValue}
                </EllipsizedTypography>
              </Tooltip>
            );
          })
        : 'Geen'}
    </>
  );
};

const StateCell = (cell: PartnerCheckedOutCellProp<'status'>) => {
  const checkoutState = {
    checkoutSettled: 'Akkoord',
    checkoutPending: 'Uren ingediend',
    noShow: 'No show',
  };
  return (
    <BoxState>
      <EllipsizedTypography variant="body1" noWrap>
        {checkoutState[cell.value]}
      </EllipsizedTypography>
    </BoxState>
  );
};

const ScheduledTimeCell = (
  cell: PartnerCheckedOutCellProp<'scheduledDateTime' | 'workedDateTime'>
) => {
  return (
    <BoxDateTime>
      <EllipsizedTypography variant="body1" color="secondary">
        {cell.value.fromDateTime} - {cell.value.toDateTime}
        {cell.value.breakMins ? `, ${cell.value.breakMins} mins` : ''}
      </EllipsizedTypography>
      <IconPauseStyled />
    </BoxDateTime>
  );
};

const DateCell = (cell: PartnerCheckedOutCellProp<'shiftStartDate'>) => {
  return (
    <Tooltip title={cell.value}>
      <EllipsizedTypography variant="body1" noWrap>
        {cell.value}
      </EllipsizedTypography>
    </Tooltip>
  );
};

const IconPauseStyled = styled(IconPause)(() => ({
  flex: 'none',
  width: '14px',
}));

const BoxDateTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
  alignItems: 'center',
}));

const BoxState = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const EllipsizedTypography = styled(Typography)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export { getPartnerCheckedOutColumnDefinition };
